import _ from 'lodash';
import {castToSisTypePrimitive} from 'util/SisType';
import {CustomFieldTO, SisType} from "generated/JavaTO"

import * as Backbone from "backbone";
import {getter, setter} from "util/BackboneCompatUtil";
import {InputType} from "module/CustomField/util/CFInputType";
import {
	getSisDateAsISO,
	getSisDateAsNumber,
	getSisDurationAsISO,
	getSisDurationAsNumber,
	getSisTimeAsISO,
	getSisTimeAsNumber,
	parseSisDuration,
	parseSisTime,
	SisDurationOptionsType,
	SisTimeOptionsType,
} from 'util/SisDateTime';
import {applySisKeyFieldRuleset, setFixedStringAt, substrTypeaware} from 'util/StringUtil';
import {CFGetOptions, CFMember, CFMemberData, CFModel, CFSetOptions, CFValue, isCFMember} from 'module/CustomField/type/CFType';
import SisDuration from 'util/SisDuration'
import {AppName, getAppName} from 'util/AppName'
import {CFAttrKey} from 'module/CustomField/constant/CFAttrConstants'
import {toFloat} from 'module/CustomField/util/CFDefUIUtil'
import {isNullCFMember} from 'module/CustomField/util/isNullCFMember'

const ensureMaxLength = (cfDef: CustomFieldTO, val: CFValue) => {
	if (CF.isCFNamespace(cfDef)) return val;
	switch (cfDef.type) {
		//number types right aligned
		case SisType.Duration:
		case SisType.Duration2:
		case SisType.Date:
		case SisType.Time:
		case SisType.Int:
		case SisType.Number:
		case SisType.Decimal:
			// cut away left part
			let valLen = (val + '').length;
			let start = valLen - cfDef.length;
			if (start < 0) start = 0;
			return substrTypeaware(val, start, valLen);
		//string types left aligned
		default:
			// cut away right part
			return substrTypeaware(val, 0, cfDef.length);
	}
};

function adaptDependentFieldValues<T extends CFModel>(cfDef: CustomFieldTO, model: T, val: null | number | string, options?: CFGetOptions) {
	if (cfDef.input === InputType.DurationAsTimeTo && options?.forView) {
		// convert duration to time to for view
		const timeFromAttr = CF.getCFAttr(cfDef, CFAttrKey.TimeFromAttr)?.value
		const timeFrom = model[timeFromAttr as keyof T]
		const t = parseSisTime(timeFrom || 0)
		if (!t.isValid()) return ''
		const dur = parseSisDuration(val as string, {userInput: true}) // val is H:mm because of forView:true
		t.add(dur)
		return getSisTimeAsISO(t)
	}
	return val
}

export const CF = {
	isCF(cfDef: CustomFieldTO) {
		return cfDef && cfDef.hasOwnProperty('id') && cfDef.hasOwnProperty('dstField');
	},

	isRawCF(cfDef: CustomFieldTO) {
		return (cfDef.input === InputType.Verbose || cfDef.input === InputType.Raw || !CF.isSingleValueCF(cfDef));
	},

	isSingleValueCF(cfDef: CustomFieldTO) {
		return cfDef.type !== SisType.Blob && cfDef.type !== SisType.GPS;
	},

	isReadOnlyInput(cfDef: CustomFieldTO) {
		return CF.isLabelField(cfDef)
	},

	isLabelField(cfDef: CustomFieldTO) {
		// skip these, because they do not set anything
		return !!cfDef.input && (
			CF.isNonValueField(cfDef) ||
			cfDef.input === InputType.Label ||
			// deprecated
			getAppName() === AppName.terminal && (cfDef.input + '')[2] === 'L'
		)
	},

	isNonValueField(cfDef: CustomFieldTO) {
		// fields, that only make sense when using in form (i.e. functional fields like buttons)
		return cfDef.input === InputType.LinkButton
	},

	isCFNamespace(cfDef: CustomFieldTO) {
		return _.startsWith(cfDef.dstField, 'cf.')
	},
	isGroupedCF(cfDef: CustomFieldTO) {
		return cfDef && cfDef.hasOwnProperty('cfGroupId') && cfDef.cfGroupId !== null;
	},
	getCFNamespaceField(cfDef: CustomFieldTO) {
		return cfDef.dstField.substr(3);
	},
	getCFAttr(cfDef: CustomFieldTO, searchKey: CFAttrKey, searchType?: string) {
		return cfDef?.cfAttrs.find(cfa => cfa.key === searchKey && (searchType === undefined || searchType === cfa.type))
	},
	hasCFAttr(cfDefs: CustomFieldTO[], searchKey: CFAttrKey, searchType?: string) {
		return cfDefs?.some(cfDef => cfDef.cfAttrs.find(cfa => cfa.key === searchKey && (searchType === undefined || searchType === cfa.type)))
	},
	/**
	 * @param {CustomFieldTO} cfDef
	 * @param {Backbone.Model} model
	 * @param {{}} [options]
	 * @param [options.pos] exact position in cf Namespace
	 * @param {boolean} [options.forView = false] set to true if you want to display the value in a text field with .val().
	 * @returns {*} new value for input field. apply this value to the field because it is the correct representation in the model
	 */
	getValueFrom<T extends CFModel>(cfDef: CustomFieldTO, model: T | null | undefined, options?: CFGetOptions): CFValue | CFMember | CFMember[] {
		if (!model) return null;
		let {dstField} = cfDef;
		if (CF.isCFNamespace(cfDef)) {
			dstField = CF.getCFNamespaceField(cfDef);
			const cf: CFMember[] = getter(model, 'cf') || []
			let ret: CFValue | CFMember | CFMember[];
			const isSingleValueCF = CF.isSingleValueCF(cfDef);
			const singleValueGetter = typeof options?.pos === 'number';
			if (singleValueGetter) {
				ret = cf.find((x: CFMember) => x.dstField === dstField && getPosFromCFMember(x) === options.pos)
			} else {
				ret = cf.filter(a => a.dstField === dstField) as CFMember[]
			}
			if (!ret) return null;
			ret = _.cloneDeep(ret);
			if (CF.isRawCF(cfDef)) {
				return ret;
			} else if (singleValueGetter) {
				return isSingleValueCF ? CF.doBeforeReturnFromGetter(cfDef, (ret as CFMember).data, options) : ret;
			} else {
				if (isSingleValueCF) {
					return (ret as CFMember[]).map((r: CFMember) => {
						r.data = CF.doBeforeReturnFromGetter(cfDef, r.data, options);
						if (r.data == null || _.isNaN(r.data)) r = _.omit(r, 'data');
						return r;
					});
				} else {
					return ret;
				}
			}
		} else {
			let rawValue = getter(model, dstField);
			if (CF.isRawCF(cfDef)) return rawValue;
			let val = substrTypeaware(rawValue, cfDef.startIndex, cfDef.length);
			val = CF.doBeforeReturnFromGetter(cfDef, val, options);
			val = adaptDependentFieldValues(cfDef, model, val, options)
			return val
		}
	},

	separateValues(cfDef: CustomFieldTO, value: string, maxAmount?: number): CFValue[] {
		const separator = CF.getCFAttr(cfDef, CFAttrKey.separator)?.value
		let values: CFValue[] = []
		if (separator) {
			values = ((value || '') + '').split(separator).filter(x => x)
		} else {
			if (value) values = [value]
		}
		return values.slice(0, maxAmount)
	},

	doBeforeReturnFromGetter(cfDef: CustomFieldTO, val: CFValue, options?: CFGetOptions) {
		val = CF.applyPrimitiveType(cfDef, val); // apply type for number field validations from text fields
		// noinspection EqualityComparisonWithCoercionJS
		if (CF.isNull(cfDef, val)) val = null;
		if (options?.forView) val = CF.transformForView(cfDef, val);
		if (val === undefined) val = null;
		return val;
	},

	/**
	 * @param {CustomFieldTO} cfDef
	 * @param {Object|Backbone.Model} model
	 * @param {*} val value from input field. Can also be valueyarndev object with 'pos' attribute (i.e. {data:' ', pos:0})
	 * @param {Object} [options] backbone set options
	 * @param {Integer} [options.pos] optional usage of pos attribute for 'cf.*' attributes, overrides 'pos'-attribute from 'val'-parameter
	 * @param {boolean} [options.userInput = false] set to true if you want apply appropriate adapt methods. requires refresh view value after using this setter.
	 * @returns {*} new value for input field. apply this value to the field because it is the correct representation in the model
	 */
	setValueTo<T extends object>(cfDef: CustomFieldTO, model: T, val: CFValue | CFMemberData, options?: CFSetOptions) {
		if (CF.isReadOnlyInput(cfDef)) return;
		let {dstField} = cfDef;
		const attr = (model instanceof Backbone.Model) ? model.attributes : model;
		if (CF.isCFNamespace(cfDef)) {
			dstField = CF.getCFNamespaceField(cfDef);
			let objVal = (_.isPlainObject(val) ? _.cloneDeep(val) : {dstField, pos: undefined, data: val}) as Partial<CFMember>
			objVal.dstField = dstField;
			if (options && typeof options.pos === 'number') {
				objVal.pos = options.pos;
			}
			if (typeof objVal.pos !== 'number' || objVal.pos < 0 || objVal.pos > 999999999) {
				throw new Error("missing pos attribute in CF.setValueTo");
			}
			objVal.data = CF.mergeAttr(cfDef, null, objVal.data, options);
			const cf = _.filter(attr.cf, x => getPosFromCFMember(x) !== objVal.pos || x.dstField !== objVal.dstField);
			if (objVal.data == null || _.isNaN(objVal.data)) objVal = _.omit(objVal, 'data');
			else objVal.data += '' // AttachmentTO.data is always string
			setter(model, 'cf', cf.concat(objVal).sort(CFMemberComparator), options);
		} else {
			if (CF.isRawCF(cfDef)) {
				setter(model, dstField, val, options);
			} else {
				const dst = _.get(attr, dstField);
				const newVal = CF.mergeAttr(cfDef, dst, (val as CFValue), options);
				setter(model, dstField, newVal, options);
			}
		}
	},

	adaptBeforeSet(cfDef: CustomFieldTO, val: CFValue, options?: CFSetOptions) {
		val = CF.parseValue(cfDef, val, options)
		val = ensureMaxLength(cfDef, val)
		val = CF.applyPrimitiveType(cfDef, val)
		if (cfDef.nullValue !== null && CF.isNull(cfDef, val)) {
			//for H, T type fields: do not display value if 0 =>=> set nullValue in cfdef
			val = cfDef.nullValue
			val = CF.applyPrimitiveType(cfDef, val)
		}
		if (cfDef.defaultValue !== null && CF.isNull(cfDef, val)) {
			val = cfDef.defaultValue
			val = CF.applyPrimitiveType(cfDef, val)
		}
		return val
	},
	/**
	 * generates model-value(combined cf values) from current value and single field value
	 * this value can be set as new one
	 * @param {boolean} [options.userInput = false] set to true if you want apply appropriate adapt methods. requires refresh view value after using this setter.
	 */
	mergeAttr(cfDef: CustomFieldTO, currAttr: CFValue, val: CFValue, options?: CFSetOptions) {
		if (CF.isReadOnlyInput(cfDef)) return currAttr;
		if (CF.isRawCF(cfDef)) return val;

		val = CF.adaptBeforeSet(cfDef, val, options)

		if (cfDef.startIndex !== 0 || (currAttr && (currAttr + '').length > cfDef.length)) {
			//either is not first value in field
			//or
			//there are more fields already set
			val = setFixedStringAt(currAttr, val, cfDef.startIndex, cfDef.length, {nullValueRepresentation: cfDef.nullValue});
			val = _.trimEnd(val);
			if (!val) val = cfDef.nullValue;
		} //else if(val === null)
		return val;
	},

	isNull(cfDef: CustomFieldTO, val: CFValue | CFMember) {
		if (isCFMember(val)) return isNullCFMember(val)
		switch (cfDef.type) {
			case SisType.Date:
				// noinspection EqualityComparisonWithCoercionJS
				if (val == 0) return true; // special value always null representation
		}
		// no strict match needed for nullValue
		// noinspection EqualityComparisonWithCoercionJS
		return val === null || val === undefined || val === '' || _.isNaN(val) || val == cfDef.nullValue;
	},

	isDefault(cfDef: CustomFieldTO, val: CFValue | CFMember) {
		return val === cfDef.defaultValue || (val as CFMember)?.data === cfDef.defaultValue;
	},

	isNullOrDefault(cfDef: CustomFieldTO, model: CFModel, pos?: number) {
		const val = CF.getValueFrom(cfDef, model, {pos}) as CFValue
		return CF.isNull(cfDef, val) || CF.isDefault(cfDef, val)
	},

	applyPrimitiveType(cfDef: CustomFieldTO, val: CFValue) {
		if (val === null || val === undefined || _.isNaN(val)) {
			return null;
		}
		val = castToSisTypePrimitive(val, cfDef.type);
		if (val === '' || _.isNaN(val)) val = null;
		return val;
	},

	parseValue(cfDef: CustomFieldTO, val: CFValue, options?: SisTimeOptionsType | SisDurationOptionsType) {
		// no strict comparison because nullValue and defaultValue are always Strings, but val can be anything
		// noinspection EqualityComparisonWithCoercionJS
		if (CF.isNull(cfDef, val)) {
			return null;
		}
		switch (cfDef.type) {
			case SisType.Key:
				val = applySisKeyFieldRuleset(val, cfDef.length);
				break;
			case SisType.Int:
			case SisType.Number:
				if (val && typeof val !== 'number') val = parseInt(val);
				break;
			case SisType.Float:
				let precision = cfDef.cfAttrs.find(cfa => cfa.key === CFAttrKey.precision)?.value
				val = toFloat(val, precision);
				break;
			case SisType.Date:
				if (val) val = getSisDateAsNumber(val);
				break;
			case SisType.Time:
				if (val) val = getSisTimeAsNumber(val, options);
				break;
			case SisType.Duration:
				if (val) val = getSisDurationAsNumber(val, options);
				break;
			case SisType.DurationA:
				if (options && options.userInput) {
					let cfAttr = CF.getCFAttr(cfDef, CFAttrKey.durationFormat)?.value || '1';
					if (val) val = SisDuration.toModel(val, parseInt(cfAttr));
				} else {
					if (val) val = getSisDurationAsNumber(val, options);
				}
				break;
		}

		if (val === '' || _.isNaN(val)) return null;
		return val;
	},

	transformForView(cfDef: CustomFieldTO, val: CFValue) {
		if (val == null) return val;
		switch (cfDef.type) {
			case SisType.Key:
				let s = val + ''
				const hasValue = s.trim().length
				if (/^[0-9]*$/g.test(s)) s = s.replace(/^0+/, '');
				if (hasValue && !s.length) s = '0' // '000000' is a valid value -> show '0' in view instead of ''
				val = s.toUpperCase();
				break;
			case SisType.Date:
				val = getSisDateAsISO(val);
				break;
			case SisType.Time:
				val = getSisTimeAsISO(val);
				break;
			case SisType.Duration:
				val = getSisDurationAsISO(val);
				break;
			case SisType.DurationA:
				let cfAttr = CF.getCFAttr(cfDef, CFAttrKey.durationFormat)?.value || '1';
				val = SisDuration.toView(val, parseInt(cfAttr));
				break;
			case SisType.Float:
				let precision = cfDef.cfAttrs.find(cfa => cfa.key === CFAttrKey.precision)?.value
				val = toFloat(val, precision);
		}
		return val;
	},

	getDqRelevantFieldNames(cfDef: CustomFieldTO) {
		return [cfDef.dstField].concat(cfDef.dqParams.map(f => f.srcName))
	},

	getInputAttributes(cfDef: CustomFieldTO) {
		// todo remove and create/use view param table (key value store mapped to cfdef) => cfattr !
		if (typeof cfDef.input !== 'string') return {};
		let idx = cfDef.input.lastIndexOf("?");
		if (idx < 0) return {};
		let str = cfDef.input.substr(idx + 1);
		return _.transform(str.split('&'), (result, p) => {
			const arr = p.split('=');
			if (arr[0] && arr[1]) {
				result[arr[0]] = arr[1];
			}
		}, {} as Record<string, string>);
	},
	getPositions(cfDefs: CustomFieldTO[], model?: CFModel | null) {
		if (!model) return []
		let allPos: (number | undefined)[] = cfDefs.flatMap(cfDef => {
			if (CF.isCFNamespace(cfDef)) {
				const dst = CF.getCFNamespaceField(cfDef)
				return model.cf?.flatMap(x => x.dstField === dst ? [getPosFromCFMember(x)] : []) || []
			} else {
				return [undefined]
			}
		})
		if (allPos.some(x => typeof x === 'number')) {
			// do not mix undefined position and number (occurs on mixing LinkButton and Group Array fields)
			allPos = allPos.filter(x => x !== undefined)
		}
		return _.uniq(allPos).sort((a, b) => (a === undefined ? -Infinity : a) - (b === undefined ? -Infinity : b))
	},
	getNextPos(cfDefs: CustomFieldTO[], model: CFModel | null) {
		cfDefs = cfDefs.filter(cfDef => !CF.isReadOnlyInput(cfDef))
		if (!cfDefs || !cfDefs.length || !model || !cfDefs.every(CF.isCFNamespace)) return undefined
		return Math.max(-1, ...CF.getPositions(cfDefs, model).filter(x => x != null) as number[]) + 1
	},
};

export function getPosFromCFMember(x: { pos: number, moveToPos?: number }): number;
export function getPosFromCFMember(x: any): number | undefined;
export function getPosFromCFMember(x: any): number | undefined {
	if (x && typeof x === 'object') {
		return typeof x.moveToPos === 'number' ? x.moveToPos : x.pos
	}
	return undefined
}
export function CFMemberComparator(a: { pos: number, moveToPos?: number }, b: { pos: number, moveToPos?: number }) {
	return (getPosFromCFMember(a) ?? -1) - (getPosFromCFMember(b) ?? -1)
}

window.sis = {
	...window.sis,
	CF,
};
